import React, { useState, useEffect } from 'react';
import './MoodTracker.css';

const MOODS = [
  { id: 'down_energized',  label: 'Feeling down\nBut energized', color: '#F9B088' },
  { id: 'good_energized',  label: 'Feeling good\nEnergized',     color: '#6BCB96' },
  { id: 'down_exhausted',  label: 'Feeling down\nExhausted',     color: '#F28BA0' },
  { id: 'good_exhausted',  label: 'Feeling good\nBut exhausted', color: '#A8B4D8' },
];

// ─── ICONS ───

function SmileyIcon({ size = 20, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  );
}

function PencilIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function CloseIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ClockIcon({ size = 16, color = '#A0ABA8' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// ─── STORAGE HELPERS ───
function todayKey() {
  return `mood_${new Date().toISOString().split('T')[0]}`;
}

function loadMood() {
  try {
    return localStorage.getItem(todayKey());
  } catch {
    return null;
  }
}

function saveMood(id) {
  try {
    localStorage.setItem(todayKey(), id);
  } catch {}
}

// ─── MODAL ───

function MoodModal({ open, onClose, todayMood, onSelect }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const todayDate = new Date().toLocaleDateString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
  });
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });

  return (
    <div className="mood-modal-overlay" onClick={onClose}>
      <div className="mood-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="mood-handle-bar" />

        <div className="mood-modal-header">
          <button className="mood-close-btn" onClick={onClose} type="button" aria-label="Close">
            <CloseIcon size={22} color="#434F4D" />
          </button>
          <div className="mood-header-center">
            <span className="mood-header-title">Feel</span>
            <span className="mood-header-date">{todayDate}</span>
          </div>
          <div style={{ width: 36 }} />
        </div>

        <h2 className="mood-question">
          How are you feeling<br />right now?
        </h2>

        <div className="mood-time-row">
          <ClockIcon size={16} color="#A0ABA8" />
          <span className="mood-time-text">{currentTime}</span>
        </div>

        <div className="mood-grid">
          {MOODS.map((mood) => {
            const isSelected = todayMood === mood.id;
            return (
              <button
                key={mood.id}
                type="button"
                className={`mood-circle ${isSelected ? 'is-selected' : ''}`}
                style={{ backgroundColor: mood.color }}
                onClick={() => onSelect(mood.id)}
              >
                <span className="mood-label">
                  {mood.label.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < mood.label.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        <button className="mood-none-btn" onClick={onClose} type="button">
          None of the above
        </button>
      </div>
    </div>
  );
}

// ─── MAIN ───

function MoodTracker() {
  const [showModal, setShowModal] = useState(false);
  const [todayMood, setTodayMood] = useState(null);

  useEffect(() => {
    setTodayMood(loadMood());
  }, []);

  const handleSelect = (moodId) => {
    saveMood(moodId);
    setTodayMood(moodId);
    setTimeout(() => setShowModal(false), 300);
  };

  const todayMoodData = MOODS.find((m) => m.id === todayMood);

  return (
    <>
      <button
        type="button"
        className="mood-card"
        onClick={() => setShowModal(true)}
      >
        <div className="mood-card-left">
          <span className="mood-card-icon">
            <SmileyIcon size={22} color="#5CB89A" />
          </span>
          <div className="mood-card-text">
            <span className="mood-card-title">
              {todayMood ? 'Mood recorded' : 'How do you feel?'}
            </span>
            {todayMoodData && (
              <span className="mood-card-sub">
                {todayMoodData.label.replace('\n', ' · ')}
              </span>
            )}
          </div>
        </div>
        <div className="mood-card-right">
          <span className="mood-card-record">Record</span>
          <PencilIcon size={14} color="#5CB89A" />
        </div>
      </button>

      <MoodModal
        open={showModal}
        onClose={() => setShowModal(false)}
        todayMood={todayMood}
        onSelect={handleSelect}
      />
    </>
  );
}

export default MoodTracker;